<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-6">
          <div>
            <h1 class="text-h4 mb-2">Gestion des fiches de poste</h1>
            <p class="text-subtitle-1 text-grey">
              Créez et gérez les fiches de poste pour vos recrutements
            </p>
          </div>
          <v-btn color="primary" prepend-icon="mdi-plus" @click="createNewJob">
            Nouvelle fiche de poste
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Filters and Search -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field v-model="searchTerm" prepend-inner-icon="mdi-magnify" label="Rechercher..." variant="outlined"
          density="compact" clearable></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-select v-model="selectedStatus" :items="statusOptions" label="Statut" variant="outlined" density="compact"
          clearable></v-select>
      </v-col>
      <v-col cols="12" md="3">
        <v-select v-model="selectedDepartment" :items="departments" label="Département" variant="outlined"
          density="compact" clearable></v-select>
      </v-col>
      <v-col cols="12" md="2">
        <v-btn variant="outlined" block @click="resetFilters">
          Réinitialiser
        </v-btn>
      </v-col>
    </v-row>

    <!-- Jobs List -->
    <v-card>
      <v-data-table :headers="headers" :items="filteredJobs" :loading="loading" class="elevation-1" item-key="id">
        <template v-slot:item.status="{ item }">
          <div class="d-flex align-center gap-1">
            <!-- <v-chip :color="getStatusColor(item.status)" size="small">
              {{ getStatusText(item.status) }}
            </v-chip> -->
            <v-tooltip v-if="item.isValidate || item.validate" text="Job validé">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" color="success" size="small">mdi-check-circle</v-icon>
              </template>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:item.applications="{ item }">
          <v-badge :content="item.numberOfCandidates" color="primary">
            <v-icon>mdi-account-group</v-icon>
          </v-badge>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon="mdi-eye" size="small" variant="text" @click="viewJob(item)"></v-btn>
          <v-btn v-if="!item.isValidate && !item.validate" icon="mdi-pencil" size="small" variant="text"
            @click="editJob(item)"></v-btn>
          <v-btn v-if="!item.isValidate && !item.validate" icon="mdi-file-check-outline" size="small" variant="text"
            color="success" @click="validateJob(item)"></v-btn>
          <v-btn icon="mdi-account-group" size="small" variant="text" color="info"
            @click="viewCandidates(item)"></v-btn>
          <v-btn v-if="!item.isValidate && !item.validate" icon="mdi-delete" size="small" variant="text" color="error"
            @click="deleteJob(item)"></v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="createJobDialog" max-width="1200px">
      <v-card>
        <v-card-title>
          {{ editingJob ? 'Modifier la fiche de poste' : 'Nouvelle fiche de poste' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="jobFormRef" v-model="formValid">
            <v-row>
              <!-- Section 1: Informations de base -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3">Informations générales</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.title" label="Titre du poste"
                  :rules="combineRules(validationRules.title, validationRules.titleLength)"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="jobFormData.contractType" :items="contractTypes" label="Type de contrat"
                  :rules="combineRules(validationRules.contractType)" variant="outlined"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.location" label="Lieu de travail"
                  :rules="combineRules(validationRules.location, validationRules.maxLength(100))"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="jobFormData.experienceLevel" :items="experienceLevelOptions"
                  label="Niveau d'expérience" variant="outlined"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="jobFormData.remotePolicy" :items="remotePolicyOptions"
                  label="Politique de télétravail" variant="outlined"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select v-model="jobFormData.industry" :items="industryOptions" label="Secteur d'activité"
                  variant="outlined"></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.salaryMin" label="Salaire minimum (€)" type="number"
                  :rules="combineRules(validationRules.salary, validationRules.positiveNumber)"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.salaryMax" label="Salaire maximum (€)" type="number"
                  :rules="combineRules(validationRules.salary, validationRules.positiveNumber, validationRules.salaryMax)"
                  variant="outlined"></v-text-field>
              </v-col>

              <!-- Section 2: Missions et environnement technique -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Missions & Environnement technique</h3>
              </v-col>

              <v-col cols="12">
                <v-combobox v-model="jobFormData.mainMissions" label="Missions principales" multiple chips clearable
                  variant="outlined" hint="Ajoutez les missions principales du poste" persistent-hint></v-combobox>
              </v-col>

              <v-col cols="12" md="6">
                <v-combobox v-model="jobFormData.techStack" label="Stack technique" multiple chips clearable
                  variant="outlined" hint="Technologies, langages, frameworks..." persistent-hint></v-combobox>
              </v-col>

              <v-col cols="12" md="6">
                <v-combobox v-model="jobFormData.toolsAndMethods" label="Outils & Méthodes" multiple chips clearable
                  variant="outlined" hint="Outils, méthodologies, processus..." persistent-hint></v-combobox>
              </v-col>

              <!-- Section 3: Description -->

              <!-- Section 4: Profil candidat -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Profil recherché</h3>
              </v-col>

              <v-col cols="12">
                <v-textarea v-model="jobFormData.candidateProfile.education" label="Formation requise"
                  variant="outlined" rows="2" hint="Diplômes, certifications, formations..."
                  persistent-hint></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-combobox v-model="jobFormData.candidateProfile.skills" label="Compétences requises" multiple chips
                  clearable variant="outlined" hint="Compétences techniques et métier" persistent-hint></v-combobox>
              </v-col>

              <v-col cols="12" md="6">
                <v-combobox v-model="jobFormData.candidateProfile.languages" label="Langues" multiple chips clearable
                  variant="outlined" hint="Langues parlées/écrites" persistent-hint></v-combobox>
              </v-col>

              <!-- Section 5: Avantages -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Avantages</h3>
              </v-col>

              <v-col cols="12">
                <v-combobox v-model="jobFormData.benefits" label="Avantages proposés" multiple chips clearable
                  variant="outlined" hint="Avantages sociaux, télétravail, formation..." persistent-hint></v-combobox>
              </v-col>

              <!-- Section 6: Lien de candidature -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Candidature</h3>
              </v-col>

              <v-col cols="12">
                <v-text-field v-model="jobFormData.applicationLink" label="Lien de candidature" variant="outlined"
                  hint="URL vers le formulaire de candidature" persistent-hint></v-text-field>
              </v-col>

              <!-- Section 7: Contenu détaillé -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Contenu détaillé</h3>
              </v-col>

              <v-col cols="12">
                <label for="job-content-md" class="mb-1 font-weight-bold">Contenu détaillé (Markdown)</label>
                <MdEditor id="job-content-md" v-model="jobFormData.content" :preview="true"
                  :toolbars-exclude="jobFormData.isValidate ? ['bold', 'italic', 'strike', 'link', 'image', 'code', 'table', 'mermaid', 'katex', 'save'] : []"
                  :readOnly="jobFormData.isValidate" language="fr-FR" style="min-height: 300px;" />
              </v-col>

              <!-- Section 8: Informations système (non modifiables) -->
              <v-col cols="12">
                <h3 class="text-h6 mb-3 mt-4">Informations système</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.jobId" label="Job ID" :disabled="true"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.besoinId" label="ID Besoin" :disabled="true"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.department" label="Département" :disabled="true"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.creationDate" label="Date de création" :disabled="true"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field v-model="jobFormData.numberOfCandidates" label="Nombre de candidats" :disabled="true"
                  variant="outlined"></v-text-field>
              </v-col>

              <v-col cols="12" md="6">
                <v-switch v-model="jobFormData.validate" label="Job validé" :disabled="true"></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">
            Annuler
          </v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!formValid" @click="saveJob">
            {{ editingJob ? 'Modifier' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Job Dialog -->
    <v-dialog v-model="viewJobDialog" max-width="900px">
      <v-card v-if="viewedJob">
        <v-card-title style="justify-content: center; text-align: center;">
          {{ viewedJob.title }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Section 1 : Informations générales -->
            <v-col cols="12">
              <h3 class="text-h6 mb-2">Informations générales</h3>
            </v-col>
            <v-col cols="12" md="6"><strong>Type de contrat :</strong> {{ viewedJob.contractType }}</v-col>
            <v-col cols="12" md="6"><strong>Département :</strong> {{ viewedJob.department }}</v-col>
            <v-col cols="12" md="6"><strong>Lieu :</strong> {{ viewedJob.location }}</v-col>
            <v-col cols="12" md="6"><strong>Secteur :</strong> {{ viewedJob.industry }}</v-col>
            <v-col cols="12" md="6"><strong>Niveau d'expérience :</strong> {{ viewedJob.experienceLevel }}</v-col>
            <v-col cols="12" md="6"><strong>Politique remote :</strong> {{ viewedJob.remotePolicy }}</v-col>
            <v-col cols="12" md="6"><strong>Date de création :</strong> {{ formatDate(viewedJob.creationDate ||
              viewedJob.createdAt) }}</v-col>
            <v-col cols="12" md="6"><strong>Lien de candidature :</strong> {{ viewedJob.applicationLink }}</v-col>
            <!-- Section 2 : Missions, stack, outils -->
            <v-col cols="12" class="mt-4">
              <h3 class="text-h6 mb-2">Missions & Environnement</h3>
            </v-col>
            <v-col cols="12" v-if="viewedJob.mainMissions && viewedJob.mainMissions.length">
              <strong>Missions principales :</strong>
              <v-list density="compact" class="d-flex flex-wrap">
                <v-list-item v-for="(item, index) in viewedJob.mainMissions" :key="index" :value="item"
                  class="col-md-6 col-lg-4">
                  <template v-slot:prepend>
                    <v-icon>mdi-circle-small</v-icon>
                  </template>
                  <v-list-item-title v-text="item"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" v-if="viewedJob.techStack && viewedJob.techStack.length">
              <strong>Stack technique :</strong>
              <v-list density="compact" class="d-flex flex-wrap">
                <v-list-item v-for="(item, index) in viewedJob.techStack" :key="index" :value="item" class="col-md-12">
                  <template v-slot:prepend>
                    <v-icon>mdi-circle-small</v-icon>
                  </template>
                  <v-list-item-title v-text="item"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" v-if="viewedJob.toolsAndMethods && viewedJob.toolsAndMethods.length">
              <strong>Outils & Méthodes :</strong>
              <v-list density="compact" class="d-flex flex-wrap">
                <v-list-item v-for="(item, index) in viewedJob.toolsAndMethods" :key="index" :value="item"
                  class="col-md-12">
                  <template v-slot:prepend>
                    <v-icon>mdi-circle-small</v-icon>
                  </template>
                  <v-list-item-title v-text="item"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>

            <!-- Section 3 : Profil recherché -->
            <v-col cols="12" class="mt-4">
              <h2 class="text-h6 mb-2">Profil recherché</h2>
            </v-col>
            <v-col cols="12" v-if="viewedJob.candidateProfile">
              <div v-if="viewedJob.candidateProfile.education"><strong>Formation :</strong>
                {{ viewedJob.candidateProfile.education }}
              </div>

              <div v-if="viewedJob.candidateProfile.skills && viewedJob.candidateProfile.skills.length">
                <strong>Compétences :</strong>
                <v-list density="compact" class="d-flex flex-wrap">
                  <v-list-item v-for="(item, index) in viewedJob.candidateProfile.skills" :key="index" :value="item"
                    class="col-md-12">
                    <template v-slot:prepend>
                      <v-icon>mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title v-text="item"></v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
              <div v-if="viewedJob.candidateProfile.languages && viewedJob.candidateProfile.languages.length">
                <strong>Langues :</strong>
                <v-list density="compact" class="d-flex flex-wrap">
                  <v-list-item v-for="(item, index) in viewedJob.candidateProfile.languages" :key="index" :value="item"
                    class="col-md-12">
                    <template v-slot:prepend>
                      <v-icon>mdi-circle-small</v-icon>
                    </template>
                    <v-list-item-title v-text="item"></v-list-item-title>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>

            <!-- Section 4 : Avantages -->
            <v-col cols="12" class="mt-4" v-if="viewedJob.benefits && viewedJob.benefits.length">
              <h3 class="text-h6 mb-2">Avantages</h3>
              <v-list density="compact" class="d-flex flex-wrap">
                <v-list-item v-for="(item, index) in viewedJob.benefits" :key="index" :value="item" class="col-md-12">
                  <template v-slot:prepend>
                    <v-icon>mdi-circle-small</v-icon>
                  </template>
                  <v-list-item-title v-text="item"></v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" class="pa-2 ma-2">
              <h3 class="text-h6 mb-2">Description</h3>
              <v-sheet class="pa-2 ma-2">
                <MdPreview v-model="viewedJob.content" :language="fr - FR" style="min-height: 300px;" />
              </v-sheet>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="viewJobDialog = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script setup>
import { useHRStore } from '@/stores/hr'
import { useSnackbarStore } from '@/stores/snackbar'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { MdEditor, MdPreview } from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const hrStore = useHRStore()
const snackbarStore = useSnackbarStore()

const loading = ref(false)
const saving = ref(false)
const createJobDialog = ref(false)
const editingJob = ref(null)
const formValid = ref(false)
const jobFormData = ref({})
const jobFormRef = ref(null)

const searchTerm = ref('')
const selectedStatus = ref('')
const selectedDepartment = ref('')

const jobs = computed(() => hrStore.jobs)

const headers = [
  { title: 'Titre', key: 'title', sortable: true },
  { title: 'Département', key: 'department', sortable: true },
  { title: 'Statut', key: 'status', sortable: true },
  { title: 'Type', key: 'contractType', sortable: true },
  { title: 'Lieu', key: 'location', sortable: true },
  { title: 'Candidatures', key: 'applications', sortable: false },
  { title: 'Créé le', key: 'createdAt', sortable: true },
  { title: 'Actions', key: 'actions', sortable: false }
]

const statusOptions = [
  { title: 'Brouillon', value: 'draft' },
  { title: 'Actif', value: 'active' },
  { title: 'En pause', value: 'paused' },
  { title: 'Fermé', value: 'closed' }
]

const contractTypes = [
  { title: 'CDI', value: 'CDI' },
  { title: 'CDD', value: 'CDD' },
  { title: 'Stage', value: 'Stage' },
  { title: 'Freelance', value: 'Freelance' },
  { title: 'Alternance', value: 'Alternance' }
]


const experienceLevelOptions = [
  { title: 'Junior', value: 'Junior' },
  { title: 'Confirmé', value: 'Confirmé' },
  { title: 'Expérimenté', value: 'Expérimenté' },
  { title: 'Senior', value: 'Senior' },
  { title: 'Expert', value: 'Expert' }
]

const remotePolicyOptions = [
  { title: 'Pas de télétravail', value: 'Pas de télétravail' },
  { title: 'Télétravail partiel', value: 'Télétravail partiel' },
  { title: 'Télétravail complet', value: 'Télétravail complet' },
  { title: 'Hybride', value: 'Hybride' }
]

const industryOptions = [
  { title: 'IT', value: 'IT' },
  { title: 'Commerce électronique', value: 'Commerce électronique' },
  { title: 'Finance', value: 'Finance' },
  { title: 'Santé', value: 'Santé' },
  { title: 'Éducation', value: 'Éducation' },
  { title: 'Manufacturing', value: 'Manufacturing' },
  { title: 'Conseil', value: 'Conseil' },
  { title: 'Automobile', value: 'Automobile' },
  { title: 'Télécommunications', value: 'Télécommunications' },
  { title: 'Énergie', value: 'Énergie' }
]


const departments = [
  'IT', 'Management', 'Commercial', 'Marketing', 'RH', 'Finance', 'Opérations'
]

const filteredJobs = computed(() => hrStore.filteredJobs)

const getStatusColor = (status) => {
  const colors = {
    draft: 'grey',
    active: 'success',
    paused: 'warning',
    closed: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status) => {
  const texts = {
    draft: 'Brouillon',
    active: 'Actif',
    paused: 'En pause',
    closed: 'Fermé'
  }
  return texts[status] || status
}

const formatDate = (date) => {
  return format(new Date(date), 'dd/MM/yyyy', { locale: fr })
}

const resetFilters = () => {
  searchTerm.value = ''
  selectedStatus.value = ''
  selectedDepartment.value = ''
}

const viewJobDialog = ref(false)
const viewedJob = ref(null)

const viewJob = (job) => {
  viewedJob.value = job
  viewJobDialog.value = true
}

const editJob = (job) => {
  editingJob.value = job
  console.log('🔧 Editing job:', job)

  // Copie profonde et sécurisée avec gestion complète de candidateProfile
  jobFormData.value = {
    // Informations de base
    id: job.id || '',
    jobId: job.job_id || job.jobId || '',
    title: job.title || '',
    contractType: job.contract_type || job.contractType || '',
    location: job.location || '',
    experienceLevel: job.experience_level || job.experienceLevel || '',
    remotePolicy: job.remote_policy || job.remotePolicy || '',
    industry: job.industry || '',

    // Salaires
    salaryMin: job.salary_min || job.salaryMin || '',
    salaryMax: job.salary_max || job.salaryMax || '',

    // Description et contenu
    description: job.description || '',
    content: job.content || '',

    // Listes modifiables - gestion sécurisée des arrays
    mainMissions: Array.isArray(job.main_missions) ? [...job.main_missions] :
      Array.isArray(job.mainMissions) ? [...job.mainMissions] : [],
    techStack: Array.isArray(job.tech_stack) ? [...job.tech_stack] :
      Array.isArray(job.techStack) ? [...job.techStack] : [],
    toolsAndMethods: Array.isArray(job.tools_and_methods) ? [...job.tools_and_methods] :
      Array.isArray(job.toolsAndMethods) ? [...job.toolsAndMethods] : [],
    benefits: Array.isArray(job.benefits) ? [...job.benefits] : [],

    // Profil candidat avec gestion sécurisée complète
    candidateProfile: {
      education: job.candidate_profile?.education || job.candidateProfile?.education || '',
      skills: Array.isArray(job.candidate_profile?.skills) ? [...job.candidate_profile.skills] :
        Array.isArray(job.candidateProfile?.skills) ? [...job.candidateProfile.skills] : [],
      languages: Array.isArray(job.candidate_profile?.languages) ? [...job.candidate_profile.languages] :
        Array.isArray(job.candidateProfile?.languages) ? [...job.candidateProfile.languages] : []
    },

    // Lien de candidature
    applicationLink: job.application_link || job.applicationLink || '',

    // Informations système (non modifiables)
    besoinId: job.besoin_id || job.besoinId || '',
    department: job.department || '',
    creationDate: job.creation_date || job.creationDate || job.createdAt || '',
    numberOfCandidates: job.number_of_candidates || job.numberOfCandidates || 0,
    validate: job.validate || false,
    isValidate: job.validate || false
  }

  console.log('✅ Job form data initialized:', jobFormData.value)
  console.log('✅ CandidateProfile:', jobFormData.value.candidateProfile)
  createJobDialog.value = true
}

const createNewJob = () => {
  editingJob.value = null
  jobFormData.value = {
    title: '',
    contractType: '',
    location: '',
    content: '',
    requirements: '',
    benefits: [],
    salaryMin: '',
    salaryMax: '',
    department: '',
    besoinId: '',
    creationDate: '',
    numberOfCandidates: 0,
    isValidate: false,
    // Initialisation complète de candidateProfile
    candidateProfile: {
      education: '',
      skills: [],
      languages: []
    },
    // Initialisation des autres listes
    mainMissions: [],
    techStack: [],
    toolsAndMethods: []
  }
  createJobDialog.value = true
}

const viewCandidates = (job) => {
  // Navigate to candidates for this job
  router.push({
    name: 'hr-candidates',
    query: { jobId: job.id }
  })
}

const deleteJob = async (job) => {
  if (confirm('Êtes-vous sûr de vouloir supprimer cette fiche de poste ?')) {
    try {
      await hrStore.deleteJob(job.id)
      await hrStore.fetchJobs()
      snackbarStore.success('Fiche de poste supprimée')
    } catch (error) {
      console.error('Error deleting job:', error)
      snackbarStore.error('Erreur lors de la suppression')
    }
  }
}

const validateJob = async (job) => {
  if (confirm('Êtes-vous sûr de vouloir valider cette fiche de poste ? Une fois validée, elle ne pourra plus être modifiée.')) {
    try {
      await hrStore.validateJob(job.id)
      await hrStore.fetchJobs()
      snackbarStore.success('Fiche de poste validée avec succès')
    } catch (error) {
      console.error('Error validating job:', error)
      snackbarStore.error('Erreur lors de la validation')
    }
  }
}

const saveJob = async () => {
  if (!formValid.value) return
  saving.value = true
  try {
    if (editingJob.value) {
      // Update via API
      console.log('Updating job:', editingJob.value.id, jobFormData.value)
      console.log('Job form ref:', jobFormRef.value)
      await hrStore.updateJob(editingJob.value.id, jobFormData.value)
      snackbarStore.success('Fiche de poste modifiée')
    } else {
      // Create via API
      await hrStore.createJob(jobFormData.value)
      snackbarStore.success('Fiche de poste créée')
    }
    await hrStore.fetchJobs()
    closeDialog()
  } catch (error) {
    console.error('Error saving job:', error)
    snackbarStore.error('Erreur lors de la sauvegarde')
  } finally {
    saving.value = false
  }
}

const closeDialog = () => {
  createJobDialog.value = false
  editingJob.value = null

  // Attendre que la modale soit fermée avant d'effacer les données
  // pour éviter les erreurs d'accès pendant le re-render
  setTimeout(() => {
    jobFormData.value = {
      candidateProfile: { education: '', skills: [], languages: [] }
    }
  }, 300)
}

const closeViewDialog = () => {
  viewJobDialog.value = false
  viewedJob.value = null
}

// Règles de validation
const validationRules = {
  required: (v) => !!v || 'Ce champ est requis',
  title: (v) => !!v || 'Le titre est requis',
  contractType: (v) => !!v || 'Le type de contrat est requis',
  location: (v) => !!v || 'Le lieu est requis',
  email: (v) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(v) || 'Email invalide'
  },
  phone: (v) => {
    const pattern = /^[0-9+\-\s()]+$/
    return !v || pattern.test(v) || 'Numéro de téléphone invalide'
  },
  salary: (v) => {
    return !v || !isNaN(v) || 'Veuillez entrer un nombre valide'
  },
  positiveNumber: (v) => {
    return !v || (Number(v) >= 0) || 'Veuillez entrer un nombre positif'
  },
  salaryMax: (v) => {
    const min = Number(jobFormData.value.salaryMin)
    const max = Number(v)
    return !v || !min || max >= min || 'Le salaire maximum doit être supérieur au minimum'
  },
  maxLength: (max) => (v) => {
    return !v || v.length <= max || `Maximum ${max} caractères autorisés`
  },
  minLength: (min) => (v) => {
    return !v || v.length >= min || `Minimum ${min} caractères requis`
  },
  titleLength: (v) => {
    return !v || (v.length >= 3 && v.length <= 100) || 'Le titre doit contenir entre 3 et 100 caractères'
  }
}

// Helper pour combiner plusieurs règles de validation
const combineRules = (...rules) => rules.filter(Boolean)

onMounted(async () => {
  loading.value = true
  console.log('🚀 JobsManagement mounted')
  try {
    await hrStore.fetchJobs()
    console.log('✅ Jobs loaded:', hrStore.jobs.length, 'items')
  } catch (error) {
    console.error('❌ Error loading jobs:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
